import React, { useState, useEffect, useRef } from 'react';
import './BulkUpload.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowLeft,
  faCloudArrowUp,
  faCheck,
  faExclamationTriangle,
  faRotate
} from '@fortawesome/free-solid-svg-icons';

function BulkUpload({ onUploadStatusChange }) {
  const [analyses, setAnalyses] = useState({});
  const [sampleTypes, setSampleTypes] = useState({});
  const [selectedAnalysis, setSelectedAnalysis] = useState('');
  const [selectedSample, setSelectedSample] = useState('');
  const [parentProjects, setParentProjects] = useState([]);
  const [subprojects, setSubprojects] = useState({});
  const [patients, setPatients] = useState([]);

  const [selectedParentId, setSelectedParentId] = useState('');
  const [selectedSubprojectId, setSelectedSubprojectId] = useState('');

  const [uploadQueue, setUploadQueue] = useState([]);
  const [uploadProgress, setUploadProgress] = useState({});
  const [errors, setErrors] = useState([]);
  const [activeUploads, setActiveUploads] = useState(0);
  
  // Error notification system with IDs and timeouts
  const [visibleErrors, setVisibleErrors] = useState([]);
  const errorTimeoutsRef = useRef({});
  
  // Clear all existing timeouts
  const clearErrorTimeouts = () => {
    Object.values(errorTimeoutsRef.current).forEach(timeout => {
      clearTimeout(timeout);
    });
    errorTimeoutsRef.current = {};
  };

  const [isDragOver, setIsDragOver] = useState(false);
  const fileInputRef = useRef(null);

  const MAX_CONCURRENT_UPLOADS = 2;
  const CHUNK_SIZE = 55 * 1024 * 1024;

  // Create a ref to store all active AbortController instances
  const activeControllersRef = useRef({});

  // 1) Fetch the supported analyses from the new backend
  useEffect(() => {
    fetch('https://test-tunnel-b1d9.szapfs.org/supported-analyses')
      .then(res => res.json())
      .then(data => {
        setAnalyses(data.analyses || {});
        setSampleTypes(data.sampleTypes || {});
      })
      .catch(err => console.error('Failed to fetch analyses:', err));
  }, []);

  // 2) Fetch parent projects from service9
  useEffect(() => {
    fetch('https://service9.szapfs.org/parent-projects')
      .then(res => res.json())
      .then(data => {
        const sorted = data.sort((a, b) => a.id - b.id);
        setParentProjects(sorted);
      })
      .catch(err => console.error('Failed to fetch parent projects:', err));
  }, []);

  // 3) If parent changes, fetch subprojects from service9
  useEffect(() => {
    if (selectedParentId) {
      fetch(`https://service9.szapfs.org/parent-project/${selectedParentId}/subprojects`)
        .then(res => res.json())
        .then(data => {
          setSubprojects(prev => ({ ...prev, [selectedParentId]: data }));
        })
        .catch(err => console.error('Failed to fetch subprojects:', err));
    }
  }, [selectedParentId]);

  // 4) If both parent and subproject are selected, fetch patients
  useEffect(() => {
    if (selectedParentId && selectedSubprojectId) {
      fetch(`https://service9.szapfs.org/project/${selectedParentId}/${selectedSubprojectId}/patients`)
        .then(res => res.json())
        .then(data => {
          const sorted = data.sort((a, b) => {
            const aNum = parseInt(a.patient_id.split('_')[2]) || 0;
            const bNum = parseInt(b.patient_id.split('_')[2]) || 0;
            return aNum - bNum;
          });
          setPatients(sorted);
        })
        .catch(err => console.error('Failed to fetch patients:', err));
    }
  }, [selectedParentId, selectedSubprojectId]);

  // Concurrency check
  useEffect(() => {
    if (uploadQueue.length > 0 && activeUploads < MAX_CONCURRENT_UPLOADS) {
      const nextItem = uploadQueue.find(
        it => !it.isUploading && !it.isComplete && !it.hasError && !it.isCanceled
      );
      if (nextItem) startUpload(nextItem);
    }
  }, [uploadQueue, activeUploads]);

  // Handle error display and automatic removal
  useEffect(() => {
    if (errors.length > 0) {
      // Clear all existing errors immediately when new errors arrive
      clearErrorTimeouts();
      setVisibleErrors([]);
      
      // Generate unique IDs for each error
      const errorsWithIds = errors.map(error => ({
        id: Date.now() + '-' + Math.random().toString(36).substr(2, 5),
        message: error,
        timestamp: Date.now()
      }));
      
      // Display each error immediately
      setVisibleErrors(errorsWithIds);
      
      // Set up removal timeouts for each error individually
      errorsWithIds.forEach(error => {
        // Each error has its own 3-second timeout
        const timeoutId = setTimeout(() => {
          setVisibleErrors(prev => prev.filter(e => e.id !== error.id));
          // Remove the timeout reference
          delete errorTimeoutsRef.current[error.id];
        }, 3000);
        
        // Store the timeout ID
        errorTimeoutsRef.current[error.id] = timeoutId;
      });
      
      // Clear the original errors array
      setErrors([]);
    }
  }, [errors]);

  const isUploading = uploadQueue.filter(item => item.isComplete).length !== uploadQueue.length && uploadQueue.length > 0;

  // Update parent component about upload status
  useEffect(() => {
    if (onUploadStatusChange) {
      onUploadStatusChange(isUploading);
    }
  }, [isUploading, onUploadStatusChange]);

  const analysisDetails = analyses[selectedAnalysis] || {};
  const isFastq = analysisDetails.uploadType === 'FASTQ';
  const allowedSamples = analysisDetails.supportedSamples || [];
  const isDropzoneEnabled = selectedAnalysis && selectedSample && selectedParentId && selectedSubprojectId;

  // Helper: upload single chunk
  const uploadChunk = async (file, chunkIndex, uploadKey, controller) => {
    const startByte = chunkIndex * CHUNK_SIZE;
    const endByte = Math.min(startByte + CHUNK_SIZE, file.size);
    const chunk = file.slice(startByte, endByte);

    const formData = new FormData();
    // These must come before file
    formData.append('uploadKey', uploadKey);
    formData.append('chunkIndex', String(chunkIndex));
    // Then the file
    formData.append('chunk', chunk);

    const res = await fetch('https://test-tunnel-b1d9.szapfs.org/upload-chunk-simple', {
      method: 'POST',
      body: formData,
      signal: controller.signal
    });
    if (!res.ok) throw new Error(`Failed to upload chunkIndex=${chunkIndex}`);
    return res.json();
  };

  // Start the entire upload for one item
  const startUpload = async (item) => {
    setActiveUploads(prev => prev + 1);

    // Create a new AbortController for this upload
    const controller = new AbortController();
    // Store the controller in our ref
    activeControllersRef.current[item.patientId] = controller;
    item.controller = controller;
    setUploadQueue(prev =>
      prev.map(q => (q.patientId === item.patientId ? { ...q, isUploading: true } : q))
    );

    // build random keys for reads
    if (!item.uploadKeyR1) {
      item.uploadKeyR1 = `${Date.now()}_${Math.random().toString(36).substring(7)}_R1`;
    }
    if (isFastq && item.files.read2 && !item.uploadKeyR2) {
      item.uploadKeyR2 = `${Date.now()}_${Math.random().toString(36).substring(7)}_R2`;
    }

    // minimal metadata
    const metadata = {
      analysisCode: selectedAnalysis,
      sampleCode: selectedSample,
      patientId: item.patient.patient_id,
      subprojectId: selectedSubprojectId,
      originalFileName: item.files.read1.name + (isFastq && item.files.read2 ? ',' + item.files.read2.name : '')
    };

    const uploadRead1 = async () => {
      const total1 = Math.ceil(item.files.read1.size / CHUNK_SIZE);
      for (let i = 0; i < total1; i++) {
        if (item.isCanceled) return;
        await uploadChunk(item.files.read1, i, item.uploadKeyR1, controller);
        setUploadProgress(prev => ({
          ...prev,
          [item.patientId]: {
            ...(prev[item.patientId] || {}),
            read1: ((i + 1) / total1) * 100
          }
        }));
      }
    };
    const uploadRead2 = async () => {
      if (!isFastq || !item.files.read2) return;
      const total2 = Math.ceil(item.files.read2.size / CHUNK_SIZE);
      for (let i = 0; i < total2; i++) {
        if (item.isCanceled) return;
        await uploadChunk(item.files.read2, i, item.uploadKeyR2, controller);
        setUploadProgress(prev => ({
          ...prev,
          [item.patientId]: {
            ...(prev[item.patientId] || {}),
            read2: ((i + 1) / total2) * 100
          }
        }));
      }
    };

    try {
      if (isFastq) {
        await Promise.all([uploadRead1(), uploadRead2()]);
      } else {
        await uploadRead1();
      }

      // finalize
      const totalChunksR1 = Math.ceil(item.files.read1.size / CHUNK_SIZE);
      let maxChunks = totalChunksR1;
      if (isFastq && item.files.read2) {
        const totalChunksR2 = Math.ceil(item.files.read2.size / CHUNK_SIZE);
        maxChunks = Math.max(maxChunks, totalChunksR2);
      }
      const finalizeBody = {
        uploadKeyR1: item.uploadKeyR1,
        uploadKeyR2: item.uploadKeyR2 || null,
        totalChunks: maxChunks,
        metadata
      };
      const finRes = await fetch('https://test-tunnel-b1d9.szapfs.org/finalize-upload-simple', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(finalizeBody)
      });
      if (!finRes.ok) throw new Error('Finalization failed');
      await finRes.json();

      setUploadQueue(prev =>
        prev.map(q => (q.patientId === item.patientId ? { ...q, isComplete: true } : q))
      );
    } catch (err) {
      if (err.name === 'AbortError') {
        console.log(`Upload canceled for patient: ${item.patientId}`);
      } else {
        console.error('Upload error:', err);
        setUploadQueue(prev =>
          prev.map(q =>
            q.patientId === item.patientId ? { ...q, hasError: true, error: err.message } : q
          )
        );
      }
    } finally {
      // Clean up the controller reference when done
      delete activeControllersRef.current[item.patientId];
      item.controller = null;
      setActiveUploads(prev => prev - 1);
    }
  };

  const cancelUpload = (item) => {
    item.isCanceled = true;
    if (item.controller) {
      item.controller.abort();
    }
    setUploadQueue(prev => prev.filter(q => q.patientId !== item.patientId));
  };

  // Cleanup function to cancel all uploads when component unmounts
  useEffect(() => {
    return () => {
      // Abort all active uploads when component unmounts
      Object.values(activeControllersRef.current).forEach(controller => {
        if (controller && typeof controller.abort === 'function') {
          controller.abort();
        }
      });
      
      console.log('BulkUpload component unmounted, all uploads aborted');
    };
  }, []);

  // Clear queue when selections change
  const handleSelectionChange = () => {
    if (uploadQueue.length > 0) {
      // Cancel all ongoing uploads
      uploadQueue.forEach(item => {
        if (item.controller && !item.isComplete && !item.isCanceled) {
          item.controller.abort();
        }
      });
      
      // Clear the queue
      setUploadQueue([]);
      setUploadProgress({});
      setActiveUploads(0);
      
    }
  };

  // Handle analysis selection change
  const handleAnalysisChange = (e) => {
    const newAnalysis = e.target.value;
    setSelectedAnalysis(newAnalysis);
    setSelectedSample('');
    handleSelectionChange();
  };

  // Handle sample type selection change
  const handleSampleChange = (e) => {
    setSelectedSample(e.target.value);
    handleSelectionChange();
  };

  // Handle parent project selection change
  const handleParentProjectChange = (e) => {
    setSelectedParentId(e.target.value);
    setSelectedSubprojectId('');
    handleSelectionChange();
  };

  // Handle subproject selection change
  const handleSubprojectChange = (e) => {
    setSelectedSubprojectId(e.target.value);
    handleSelectionChange();
  };

  const validateFiles = (files) => {
    const newErrors = [];
    const grouped = {};
    const finalQueue = [];
    const validPatientIds = patients.map(p => p.patient_id);
    
    // Create a map of existing file names in the queue for quick lookup
    const existingFileNames = new Set(uploadQueue.map(item => 
      isFastq 
        ? [item.files.read1?.name, item.files.read2?.name].filter(Boolean)
        : [item.files.read1?.name].filter(Boolean)
    ).flat());

    // For non-FASTQ, track patient IDs that are already in the queue
    const existingPatientIds = new Set(
      !isFastq ? uploadQueue.map(item => item.patientId) : []
    );

    for (let file of files) {
      // Skip duplicates (file names)
      if (existingFileNames.has(file.name)) {
        newErrors.push(`Duplicate file "${file.name}" already in queue`);
        continue;
      }
      
      const baseName = file.name.replace(/\.[^/.]+$/, '');
      const patientNumber = baseName.split('_')[0];
      const paddedParent = String(selectedParentId).padStart(2, '0');
      const fullPatientId = `${paddedParent}_${selectedSubprojectId}_${patientNumber}`;

      if (!validPatientIds.includes(fullPatientId)) {
        newErrors.push(`Invalid file "${file.name}": patient "${patientNumber}" not found`);
        continue;
      }
      
      // For non-FASTQ, check for duplicate patient IDs
      if (!isFastq && existingPatientIds.has(fullPatientId)) {
        newErrors.push(`Patient "${patientNumber}" already has a file in the queue`);
        continue;
      }
      
      if (!grouped[fullPatientId]) {
        grouped[fullPatientId] = { files: {} };
      }
      const isR1 = file.name.includes('R1');
      const isR2 = file.name.includes('R2');
      if (isFastq) {
        if (!isR1 && !isR2) {
          newErrors.push(`File "${file.name}" must have R1 or R2 in name for FASTQ`);
          continue;
        }
        if (isR1) grouped[fullPatientId].files.read1 = file;
        if (isR2) grouped[fullPatientId].files.read2 = file;
      } else {
        if (!grouped[fullPatientId].files.read1) {
          grouped[fullPatientId].files.read1 = file;
          existingPatientIds.add(fullPatientId); // Track this patient ID
        } else {
          newErrors.push(`Extra file "${file.name}" for single-file analysis (patient "${patientNumber}")`);
        }
      }
    }

    // build finalQueue
    for (const pid of Object.keys(grouped)) {
      const pObj = patients.find(p => p.patient_id === pid);
      if (!pObj) continue;
      const { read1, read2 } = grouped[pid].files;
      if (isFastq) {
        if (!read1 || !read2) {
          newErrors.push(`Missing R1 or R2 for patient "${pid.split('_')[2]}"`);
          continue;
        }
        finalQueue.push({ patientId: pid, patient: pObj, files: { read1, read2 } });
      } else {
        if (read1) {
          finalQueue.push({ patientId: pid, patient: pObj, files: { read1 } });
        }
      }
    }

    setErrors(newErrors);

    const queueItems = finalQueue.map(it => ({
      ...it,
      isUploading: false,
      isComplete: false,
      hasError: false,
      isCanceled: false,
      error: null,
      uploadKeyR1: null,
      uploadKeyR2: null,
      progress: 0
    }));

    setUploadQueue(prev => [...prev, ...queueItems]);

    const newProgress = {};
    queueItems.forEach(i => {
      newProgress[i.patientId] = {
        read1: 0,
        ...(isFastq ? { read2: 0 } : {})
      };
    });
    setUploadProgress(prev => ({...prev, ...newProgress}));
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (isDropzoneEnabled) {
      e.dataTransfer.dropEffect = 'copy';
      setIsDragOver(true);
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (!isDropzoneEnabled) return;
    const droppedFiles = e.dataTransfer?.files || e.target.files;
    if (droppedFiles?.length) {
      validateFiles(droppedFiles);
    }
    setIsDragOver(false);
  };

  return (
    <div className={`bulk-upload-container ${isUploading ? 'uploading-in-progress' : ''}`}>
      <div className="bulk-upload-grid">
          <div className="bulk-dropdown">
            <select
              value={selectedAnalysis}
              onChange={handleAnalysisChange}
            >
              <option value="">Analysis Type</option>
              {Object.entries(analyses).map(([code, details]) => (
                <option key={code} value={code}>
                  {details.name} ({code})
                </option>
              ))}
            </select>
          </div>
          <div className="bulk-dropdown">
            <select
              value={selectedSample}
              onChange={handleSampleChange}
              disabled={!selectedAnalysis}
            >
              <option value="">Sample Type</option>
              {Object.entries(sampleTypes)
                .filter(([smCode]) => allowedSamples.includes(smCode))
                .map(([smCode, sm]) => (
                  <option key={smCode} value={smCode}>
                    {sm.name} ({smCode})
                  </option>
                ))}
            </select>
          </div>
          <div className="bulk-dropdown">
            <select
              value={selectedParentId}
              onChange={handleParentProjectChange}
            >
              <option value="">Project</option>
              {parentProjects.map(pp => (
                <option key={pp.id} value={pp.id}>
                  {pp.name}
                </option>
              ))}
            </select>
          </div>
          <div className="bulk-dropdown">
            <select
              value={selectedSubprojectId}
              onChange={handleSubprojectChange}
              disabled={!selectedParentId}
            >
              <option value="">Subproject</option>
              {(subprojects[selectedParentId] || []).map(sp => (
                <option key={sp.project_id} value={sp.project_id}>
                  {sp.project_id} - ({sp.description})
                </option>
              ))}
            </select>
          </div>
    
      </div>

      <div 
        className={`drop-zone ${!isDropzoneEnabled ? 'disabled' : ''} ${isDragOver ? 'drag-over' : ''}`}
        onDragOver={isDropzoneEnabled ? handleDragOver : null}
        onDragLeave={isDropzoneEnabled ? handleDragLeave : null}
        onDrop={isDropzoneEnabled ? handleDrop : null}
        onClick={isDropzoneEnabled ? () => fileInputRef.current?.click() : null}
      >
        <input
          type="file"
          ref={fileInputRef}
          style={{ display: 'none' }}
          onChange={(e) => handleDrop(e)}
          multiple
          disabled={!isDropzoneEnabled}
        />
        <FontAwesomeIcon icon={faCloudArrowUp} className="upload-icon" />
        <div className="drop-zone-text">
          {isDropzoneEnabled ? (
            <>
              <p>Drag &amp; drop your files here, or click to select files</p>
              <p className="drop-zone-subtext">
                {isFastq 
                  ? "For FASTQ, ensure each patient has both R1 and R2 files" 
                  : "Each patient should have a single file"}
              </p>
            </>
          ) : (
            <>
              <p>Please complete all selections first</p>
              <p className="drop-zone-subtext">
                Select Analysis Type, Sample Type, Project and Subproject to enable file upload
              </p>
            </>
          )}
          {errors.length > 0 && (
            <div className="upload-errors">
              <h4>Upload Issues:</h4>
              <ul>
                {errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
      <div className="upload-grid-container">

        <div className="upload-grid">
          {[...uploadQueue].sort((a, b) => {
            // Active uploads first
            if (!a.isComplete && !a.isCanceled && !a.hasError && a.isUploading) return -1;
            if (!b.isComplete && !b.isCanceled && !b.hasError && b.isUploading) return 1;
            
            // Pending uploads next
            if (!a.isComplete && !a.isCanceled && !a.hasError) return -1;
            if (!b.isComplete && !b.isCanceled && !b.hasError) return 1;
            
            // Completed uploads last
            return 0;
          }).map((item, index) => {
            return (
              <div 
                key={item.patientId} 
                className={`upload-item
                  ${item.isComplete ? 'complete' : ''}
                  ${item.hasError ? 'error' : ''}
                  ${item.isCanceled ? 'canceled' : ''}
                  ${(!item.isComplete && !item.isCanceled && !item.hasError && item.isUploading) ? 'active-upload' : ''}
                `}
                style={{ 
                  opacity: (!item.isComplete && !item.isCanceled && !item.hasError && item.isUploading) ? 1 : 0.7
                }}
              >
                {(!item.isComplete && !item.isCanceled && !item.hasError && item.isUploading) && (
                  <div className="uploading-indicator">
                    <FontAwesomeIcon icon={faRotate} spin />
                    <span>Uploading</span>
                  </div>
                )}
                <div className="patient-info">
                  <span className="patient-name">{item.patient.name}</span>
                  <span className="patient-id">{item.patient.patient_id}</span>
                </div>
                <div className="file-info">
                  <div className="file-progress">
                    <span>R1: {item.files.read1.name}</span>
                    <div className="progress-bar">
                      <div
                        className="progress"
                        style={{ width: `${uploadProgress[item.patientId].read1 || 0}%` }}
                      />
                    </div>
                  </div>
                  {isFastq && item.files.read2 && (
                    <div className="file-progress">
                      <span>R2: {item.files.read2.name}</span>
                      <div className="progress-bar">
                        <div
                          className="progress"
                          style={{ width: `${uploadProgress[item.patientId].read2 || 0}%` }}
                        />
                      </div>
                    </div>
                  )}
                </div>
                {item.hasError && (
                  <div className="error-message">
                    <FontAwesomeIcon icon={faExclamationTriangle} /> {item.error}
                  </div>
                )}
                {item.isComplete && !item.hasError && (
                  <div className="complete-icon">
                    <FontAwesomeIcon icon={faCheck} />
                  </div>
                )}
                {!item.isComplete && !item.isCanceled && !item.hasError && (
                  <button className="cancel-upload-btn" onClick={() => cancelUpload(item)}>
                    Cancel
                  </button>
                )}
              </div>
            );
          })}
        </div>
        </div>
        <div className="bulk-upload-information">{uploadQueue.filter(item => item.isComplete).length}/{uploadQueue.length} files uploaded</div>

        <div className="errorbulk-container">
          {visibleErrors.map((error) => (
            <div key={error.id} className="errorbulk-notification">
              <FontAwesomeIcon icon={faExclamationTriangle} />
              <span>{error.message}</span>
            </div>
          ))}
        </div>
    </div>
  );
}

export default BulkUpload;
